/**
 * Geltung — which articles a unit's statement speaks for, as Studio V2 presents
 * it — mapped onto the ONE thing the server persists: a statement's scope
 * (`scope_all` or an explicit article subset via PUT /statements/{id}/scope).
 *
 * Five display values (the concept's Ebene 4), three persisted realities:
 *   all       ↔ scope_all = true
 *   list      ↔ an explicit article subset
 *   group     — presentation variant of `list`/`all` (the document speaks per
 *   material  — product group / compound); SAME write as `list`. Never stored.
 *   ask       — the scope is unresolvable → Rückfrage. No scope write at all;
 *               the unresolved scope already classifies red server-side.
 */

export type Geltung = "all" | "list" | "group" | "material" | "ask"

export interface GeltungFacts {
  scopeAll: boolean
  selectedCount: number
  unresolvedRefs: number
  /** An open Rückfrage for the unit's framework (GET .../inquiries). */
  inquiryOpen: boolean
  /** Collected but unsent questions on this very statement (the W5-1
   *  collector, `mc_declaration_questions` drafts). A statement the reviewer
   *  is asking about IS "vorbehaltlich Rückfrage", and unlike the local
   *  manual pick this fact survives a sheet, a regulation switch and a
   *  reload (live test, wave 5). Optional so older callers keep compiling. */
  draftQuestions?: boolean
}

/** The Geltung a unit's persisted facts DERIVE to. `group`/`material` never
 *  come back from data — they are how the reviewer chose to read a `list`. */
export function deriveGeltung(
  f: GeltungFacts,
): Exclude<Geltung, "group" | "material"> {
  if (f.inquiryOpen || f.draftQuestions || f.unresolvedRefs > 0) return "ask"
  if (f.scopeAll) return "all"
  return "list"
}

/**
 * The scope write a Geltung change requires, or null when nothing is written
 * (`ask` opens the Rückfrage composer instead; an unchanged value is a no-op).
 *
 * THE ONE-WAY DOOR, stated rather than papered over: the server has no call
 * that sets `scope_all` back to true — `update_statement_scope` unconditionally
 * persists an explicit subset (the subset IS the step-2 signature). Picking
 * "Alle Artikel" after a subset was ever applied therefore writes the CURRENT
 * pool as the subset (`kind: "pool-snapshot"`). Functionally identical today,
 * but it freezes the statement to today's pool instead of fanning out as the
 * pool grows — the UI copy for this case must say "alle aktuellen Artikel des
 * Lieferanten", never promise future coverage. Real reversion is a filed
 * backend gap.
 */
export type ScopeWrite =
  | { kind: "subset"; articleNumbers: string[] }
  | { kind: "pool-snapshot"; articleNumbers: string[] }

export function scopeWriteFor(
  next: Geltung,
  opts: {
    /** Derived Geltung of the persisted state (deriveGeltung). */
    current: Exclude<Geltung, "group" | "material">
    /** The declaration's full resolved article pool (public numbers). */
    poolNumbers: readonly string[]
    /** The reviewer's article selection for subset writes. */
    selection: readonly string[]
  },
): ScopeWrite | null {
  if (next === "ask") return null
  if (next === "all") {
    // Already genuinely scope_all → nothing to write; otherwise the snapshot.
    if (opts.current === "all") return null
    // The SERVER resolves the pool (`useApplyStatementPoolScope`, a body-less
    // command); `articleNumbers` here is only what the card shows while the
    // write is in flight, never sent on the wire. Shipping the pool broke on a
    // 10,582-article org (request cap 10,000).
    return {
      kind: "pool-snapshot",
      articleNumbers: [...opts.poolNumbers],
    }
  }
  // list / group / material — one and the same persisted shape (an explicit
  // subset). NEVER an EMPTY one, though: `opts.selection` reads
  // contractually empty right after a genuinely `scope_all` statement
  // (`StatementGroupEntry`'s own doc — a scopeAll entry's own
  // `articleNumbers` is ALWAYS empty), so a caller invoking this the INSTANT
  // the reviewer switches Geltung away from "Alle Artikel" — before they
  // have chosen a single article — would otherwise persist an empty subset
  // on one click, no staging. `deriveFrameworkVerdict`'s `inScope` then
  // drops the statement from scope entirely, silently flipping a
  // non-compliant framework to (vacuously) compliant. A REAL selection
  // (the reviewer has actually picked something — `ArticleListSlot`'s own
  // Apply, or `ArticleSlot`'s Mine/Not-mine toggle) still writes through
  // exactly as before; this only refuses the empty one.
  if (opts.selection.length === 0) return null
  return { kind: "subset", articleNumbers: [...opts.selection] }
}
